import React, { useEffect, useState } from "react";
import axios from "axios";
import "../styles/Blogs.css";

const CreateBlog = () => {
  const [loading, setLoading] = useState(false);
  const [image, setImage] = useState(null);
  const [blogs, setBlogs] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingBlog, setEditingBlog] = useState(null);
  const [isTableView, setIsTableView] = useState(false);

  const [formData, setFormData] = useState({
    title: "",
    content: "",
  });

  /* ================= FETCH BLOGS ================= */
  const fetchAllBlogs = async () => {
    try {
      const res = await axios.get("/api/v1/blogs/getblogs");
      setBlogs(res.data.blogs);
    } catch {
      alert("Failed to fetch blogs");
    }
  };

  /* ================= IMAGE HANDLER ================= */
  const handleImageChange = (e) => {
    setImage(e.target.files[0]);
  };

  /* ================= CREATE BLOG ================= */
  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("token");
    if (!token) return alert("Please login first");

    const data = new FormData();
    data.append("title", formData.title);
    data.append("content", formData.content);
    if (image) data.append("image", image);

    try {
      setLoading(true);
      await axios.post("/api/v1/blogs/", data, {
        headers: { "x-auth-token": token },
      });
      alert("Blog created successfully");
      setFormData({ title: "", content: "" });
      setImage(null);
      fetchAllBlogs();
    } catch {
      alert("Failed to create blog");
    } finally {
      setLoading(false);
    }
  };

  /* ================= DELETE BLOG ================= */
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this blog?")) return;

    try {
      await axios.delete(`/api/v1/blogs/admin/deleteblogs/${id}`, {
        headers: { "x-auth-token": localStorage.getItem("token") },
      });
      fetchAllBlogs();
      alert("Blog deleted");
    } catch {
      alert("Failed to delete blog");
    }
  };

  /* ================= EDIT BLOG ================= */
  const handleEdit = (blog) => {
    setEditingBlog(blog);
    setFormData({ title: blog.title, content: blog.content });
    setIsModalVisible(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("token");

    const data = new FormData();
    data.append("title", formData.title);
    data.append("content", formData.content);
    if (image) data.append("image", image);

    try {
      setLoading(true);
      await axios.put(
        `/api/v1/blogs/admin/updateblogs/${editingBlog._id}`,
        data,
        { headers: { "x-auth-token": token } }
      );
      alert("Blog updated");
      fetchAllBlogs();
      setIsModalVisible(false);
      setEditingBlog(null);
    } catch {
      alert("Failed to update blog");
    } finally {
      setLoading(false);
    }
  };

  /* ================= STATS ================= */
  const totalBlogs = blogs.length;
  const blogsWithImages = blogs.filter((b) => b.image).length;
  const blogsWithoutImages = totalBlogs - blogsWithImages;

  useEffect(() => {
    fetchAllBlogs();
  }, []);

  return (
    <div className="create-blog">
      <h2>Create Blog</h2>

      {/* ===== CREATE FORM ===== */}
      <form className="create-blog-form" onSubmit={handleSubmit}>
        <input
          type="text"
          placeholder="Title"
          value={formData.title}
          required
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
        />

        <textarea
          placeholder="Content"
          rows="4"
          value={formData.content}
          required
          onChange={(e) =>
            setFormData({ ...formData, content: e.target.value })
          }
        />

        <input type="file" onChange={handleImageChange} />

        <button className="btn-primary" disabled={loading}>
          {loading ? "Submitting..." : "Submit"}
        </button>
      </form>

      {/* ===== BLOG STATS ===== */}
      <div className="blog-stats">
        <p>Total Blogs: {totalBlogs}</p>
        <p>With Images: {blogsWithImages}</p>
        <p>Without Images: {blogsWithoutImages}</p>
      </div>

      {/* ===== VIEW TOGGLE ===== */}
      <label className="toggle">
        <input
          type="checkbox"
          checked={isTableView}
          onChange={() => setIsTableView(!isTableView)}
        />
        <span>{isTableView ? "Table View" : "Grid View"}</span>
      </label>

      {/* ===== BLOG LIST ===== */}
      {isTableView ? (
        <table className="blogs-table">
          <thead>
            <tr>
              <th>Title</th>
              <th>Content</th>
              <th>Image</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {blogs.map((blog) => (
              <tr key={blog._id}>
                <td>{blog.title}</td>
                <td>{blog.content}</td>
                <td>
                  {blog.image ? (
                    <img
                      src={`https://res.cloudinary.com/dnxicetc5/image/upload/${blog.image}`}
                      alt=""
                    />
                  ) : (
                    "No Image"
                  )}
                </td>
                <td>
                  <button onClick={() => handleEdit(blog)}>Edit</button>
                  <button
                    className="delete-btn"
                    onClick={() => handleDelete(blog._id)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div className="blogs-grid">
          {blogs.map((blog) => (
            <div className="blog-card" key={blog._id}>
              <h3>{blog.title}</h3>
              <p>{blog.content}</p>
              {blog.image && (
                <img
                  src={`https://res.cloudinary.com/dnxicetc5/image/upload/${blog.image}`}
                  alt=""
                />
              )}
              <div className="blog-actions">
                <button onClick={() => handleEdit(blog)}>Edit</button>
                <button
                  className="delete-btn"
                  onClick={() => handleDelete(blog._id)}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ===== EDIT MODAL ===== */}
      {isModalVisible && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Edit Blog</h3>
            <form onSubmit={handleUpdate}>
              <input
                type="text"
                value={formData.title}
                required
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
              />
              <textarea
                rows="4"
                value={formData.content}
                required
                onChange={(e) =>
                  setFormData({ ...formData, content: e.target.value })
                }
              />
              <input type="file" onChange={handleImageChange} />
              <button className="btn-primary">Update</button>
              <button type="button" onClick={() => setIsModalVisible(false)}>
                Cancel
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateBlog;
